import { Product } from '../types/Product';
import getProducts from '../../graphql/utils/getProducts';
import getLanguage from '../utils/getLanguage';
import { logInfo, logError } from '../utils/logging';
import { REGISTERED_APPS } from '../types/Apps';

const RECOMM_ENGINE_DOMAIN = 'recomm-engine.digismoothie.app';

/**
 * Represents a single recommendation from the Digismoothie Recommendation Engine.
 */
export interface DSRecommendation {
  shopify_product_id: string;
  description?: string;
}

/**
 * Builds the full URL for the recomm-engine API request.
 * @param shopDomain - The myshopify domain of the shop
 * @param productId - The Shopify product ID for which to fetch recommendations
 * @param locale - The locale string (e.g., 'EN')
 * @returns The full recomm-engine API URL
 */
function buildRecommEngineURL(
  shopDomain: string,
  productId: number,
  locale: string,
): string {
  return `https://${RECOMM_ENGINE_DOMAIN}/api/recommendations/?shop=${encodeURIComponent(
    shopDomain,
  )}&product_id=${encodeURIComponent(productId)}&locale=${encodeURIComponent(
    locale,
  )}`;
}

/**
 * Fetches raw recommendations from the Digismoothie Recommendation Engine API.
 * @param shopDomain - The myshopify domain of the shop
 * @param productId - The Shopify product ID for which to fetch recommendations
 * @param locale - The locale string (e.g., 'EN')
 * @returns An array of DSRecommendation objects
 */
export async function fetchDSRecommendations(
  shopDomain: string,
  productId: number,
  locale: string,
  runningAppName: string,
): Promise<DSRecommendation[]> {
  const url = buildRecommEngineURL(shopDomain, productId, locale);
  logInfo(
    `Fetching recommended products from DS Recomm Engine: ${url}`,
    runningAppName as REGISTERED_APPS,
  );

  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    logError(
      `Failed to fetch recommended products: ${response.status} ${response.statusText}`,
      runningAppName as REGISTERED_APPS,
    );
    return [];
  }
  const data = await response.json();
  if (!Array.isArray(data)) {
    logError(
      'Invalid response format: expected an array',
      runningAppName as REGISTERED_APPS,
    );
    return [];
  }
  return data;
}

/**
 * Extracts valid Shopify product IDs from the recomm-engine API response.
 * @param response - The array of DSRecommendation objects
 * @returns An array of valid Shopify product IDs (numbers)
 */
export function extractProductIdsFromDSResponse(
  response: DSRecommendation[],
): number[] {
  return response
    .map((item) => {
      const id = parseInt(item.shopify_product_id, 10);
      return !isNaN(id) && id > 0 ? id : null;
    })
    .filter((id): id is number => id !== null);
}

/**
 * Fetches recommended products from the recomm-engine, then loads their details from Shopify.
 * Filters and limits the results as needed.
 *
 * @param productId - The Shopify product ID for which to fetch recommendations
 * @param ignoreTag - A tag to filter out from the results
 * @param limit - The maximum number of products to return
 * @param storefrontAccessToken - The Storefront API access token
 * @returns An array of Product objects with full details
 */
export async function getDSRecommendedProducts(
  productId: number,
  ignoreTag: string,
  limit: number,
  storefrontAccessToken: string,
  runningAppName: string,
): Promise<Product[]> {
  const myshopifyDomain = window.Shopify.shop || '';
  const locale = getLanguage()?.toUpperCase() ?? 'EN';

  try {
    const recommendations = await fetchDSRecommendations(
      myshopifyDomain,
      productId,
      locale,
      runningAppName,
    );
    const productIDs = extractProductIdsFromDSResponse(recommendations);

    if (productIDs.length === 0) {
      logError(
        'No valid product IDs found in the response',
        runningAppName as REGISTERED_APPS,
      );
      return [];
    }
    const products = await getProducts(
      storefrontAccessToken,
      productIDs,
      runningAppName,
    );
    if (!products || products.length === 0) {
      logError(
        'No products found for the given product IDs',
        runningAppName as REGISTERED_APPS,
      );
      return [];
    }
    let visibleProducts = products.filter(
      (product) => !product.tags.includes(ignoreTag),
    );
    if (visibleProducts.length > limit) {
      visibleProducts = visibleProducts.slice(0, limit);
    }
    return visibleProducts;
  } catch (e) {
    logError(e, runningAppName as REGISTERED_APPS);
    return [];
  }
}

/**
 * Determines if the DS Recommendation Engine should be used, based on localStorage and BO flag.
 * @param storefrontAccessToken - The Storefront API access token
 * @param useDSRecommEngine - The BO flag for using the recomm engine
 * @returns true if recomm engine should be used, false otherwise
 */
export function shouldUseDSRecommEngine(
  storefrontAccessToken?: string,
  useDSRecommEngine?: boolean,
): boolean {
  const USE_DS_RECOMM_ENGINE_LOCALSTORAGE_KEY =
    'candyrack-use-ds-recomm-engine';
  const localOverride = localStorage.getItem(
    USE_DS_RECOMM_ENGINE_LOCALSTORAGE_KEY,
  );
  return (
    (localOverride === 'true' || useDSRecommEngine === true) &&
    typeof storefrontAccessToken === 'string' &&
    storefrontAccessToken.trim() !== ''
  );
}
